import { defineArrayMember, defineField, defineType } from "sanity";
import { US_STATES } from "@/lib/stateTaxRates";

const HEX_COLOR_REGEX = /^#([0-9a-fA-F]{3}){1,2}$/;

export const siteSettings = defineType({
  name: "siteSettings",
  title: "Site Settings",
  type: "document",
  groups: [
    { name: "branding", title: "Branding", default: true },
    { name: "collections", title: "Collections" },
    { name: "footer", title: "Footer" },
    { name: "newsletter", title: "Newsletter" },
    { name: "itemActions", title: "Add-to-Bag Buttons" },
    { name: "swipeDeckPopup", title: "Swipe Deck Popup" },
    { name: "onboarding", title: "Onboarding Modal" },
    { name: "invoices", title: "Invoices" }
  ],
  fields: [
    defineField({
      name: "title",
      title: "Title",
      type: "string",
      hidden: true
    }),
    defineField({
      name: "brandName",
      title: "Brand name",
      type: "string",
      description: "Shown in the header and footer.",
      validation: (Rule) => Rule.required().error("Add the brand name visitors should see."),
      group: "branding"
    }),
    defineField({
      name: "logo",
      title: "Logo",
      type: "image",
      description: "Upload a transparent logo for the header.",
      options: { hotspot: true },
      fields: [
        defineField({
          name: "alt",
          title: "Alt text",
          type: "string",
          description: "Describe the logo for screen readers.",
          validation: (Rule) =>
            Rule.custom((value) => (value ? true : "Add alt text so the logo is accessible.")).warning(
              "Add alt text so the logo is accessible."
            )
        })
      ],
      group: "branding"
    }),
    defineField({
      name: "seoTitle",
      title: "SEO title",
      type: "string",
      description: "Shown in browser tabs and search results.",
      group: "branding"
    }),
    defineField({
      name: "seoDescription",
      title: "SEO description",
      type: "text",
      rows: 3,
      description: "Short description for search engines.",
      group: "branding"
    }),
    defineField({
      name: "ogImage",
      title: "Default sharing image",
      type: "image",
      description: "Default image shown when pages are shared on social media. Individual pages can override this.",
      options: { hotspot: true },
      fields: [
        defineField({
          name: "alt",
          title: "Alt text",
          type: "string",
          description: "Describe the image for screen readers."
        })
      ],
      group: "branding"
    }),
    defineField({
      name: "primaryColor",
      title: "Primary color",
      type: "string",
      description: "Hex color used for buttons and highlights (example: #c4a668).",
      validation: (Rule) =>
        Rule.required()
          .custom((value) => (value && HEX_COLOR_REGEX.test(value) ? true : "Use a valid hex color like #c4a668."))
          .error("Use a valid hex color like #c4a668."),
      group: "branding"
    }),
    defineField({
      name: "secondaryColor",
      title: "Secondary color",
      type: "string",
      description: "Hex color used for accents and borders.",
      validation: (Rule) =>
        Rule.required()
          .custom((value) => (value && HEX_COLOR_REGEX.test(value) ? true : "Use a valid hex color like #1e1b16."))
          .error("Use a valid hex color like #1e1b16."),
      group: "branding"
    }),
    defineField({
      name: "headerFontFamily",
      title: "Header font family",
      type: "string",
      description: "Font used for headings and display text.",
      options: {
        list: [
          { title: "Inter", value: "inter" },
          { title: "Playfair", value: "playfair" },
          { title: "Lora", value: "lora" },
          { title: "Cormorant Garamond", value: "cormorant-garamond" },
          { title: "Montserrat", value: "montserrat" }
        ]
      },
      validation: (Rule) => Rule.required().error("Choose a header font."),
      group: "branding"
    }),
    defineField({
      name: "bodyFontFamily",
      title: "Body font family",
      type: "string",
      description: "Font used for paragraphs and body text.",
      options: {
        list: [
          { title: "Inter", value: "inter" },
          { title: "Playfair", value: "playfair" },
          { title: "Lora", value: "lora" },
          { title: "Cormorant Garamond", value: "cormorant-garamond" },
          { title: "Montserrat", value: "montserrat" }
        ]
      },
      validation: (Rule) => Rule.required().error("Choose a body font."),
      group: "branding"
    }),
    defineField({
      name: "fontFamily",
      title: "Legacy font family",
      type: "string",
      hidden: true
    }),
    defineField({
      name: "baseFontScale",
      title: "Base font scale",
      type: "number",
      description: "Adjust the overall font size (1.0 = default).",
      validation: (Rule) =>
        Rule.min(0.9)
          .max(1.2)
          .precision(2)
          .error("Pick a value between 0.9 and 1.2."),
      initialValue: 1,
      group: "branding"
    }),
    defineField({
      name: "collectionsMenuTitle",
      title: "Collections menu title",
      type: "string",
      description: "Top-level section label shown in the side menu.",
      validation: (Rule) => Rule.required().error("Add the side menu section title."),
      group: "collections"
    }),
    defineField({
      name: "collectionsMenuItems",
      title: "Collections menu items",
      type: "array",
      description: "Collections shown in the side menu. The name must match the collection option in Airtable.",
      of: [
        defineField({
          name: "collectionItem",
          title: "Collection",
          type: "object",
          fields: [
            defineField({
              name: "name",
              title: "Collection name",
              type: "string",
              description: "Must match the collection value in Airtable exactly.",
              validation: (Rule) => Rule.required().error("Add the collection name.")
            }),
            defineField({
              name: "heroImage",
              title: "Hero image",
              type: "image",
              description: "Optional banner image shown at the top of this collection's page.",
              options: { hotspot: true },
              fields: [
                defineField({
                  name: "alt",
                  title: "Alt text",
                  type: "string",
                  description: "Describe the image for screen readers."
                })
              ]
            }),
            defineField({
              name: "description",
              title: "Description",
              type: "text",
              rows: 3,
              description: "Optional copy shown below the hero on this collection's page."
            })
          ],
          preview: {
            select: { title: "name", media: "heroImage" }
          }
        })
      ],
      group: "collections"
    }),
    defineField({
      name: "footerTagline",
      title: "Footer tagline",
      type: "text",
      rows: 2,
      group: "footer",
      description: "Short tagline shown under the brand name in the footer."
    }),
    defineField({
      name: "privateShowingsHeading",
      title: "Private showings heading",
      type: "string",
      group: "footer",
      description: "Heading for the private showings section in the footer."
    }),
    defineField({
      name: "privateShowingsBody",
      title: "Private showings body",
      type: "text",
      rows: 2,
      group: "footer",
      description: "Body text for the private showings section in the footer."
    }),
    defineField({
      name: "privateShowingsCtaText",
      title: "Private showings CTA text",
      type: "string",
      group: "footer",
      description: "Button text for the private showings call-to-action."
    }),
    defineField({
      name: "privateShowingsCtaLink",
      title: "Private showings CTA link",
      type: "string",
      group: "footer",
      description: "Link for the private showings call-to-action (e.g. /contact)."
    }),
    defineField({
      name: "footerExploreLabel",
      title: "Footer explore label",
      type: "string",
      group: "footer",
      description: "Label for the Explore column in the footer (e.g. 'Explore')."
    }),
    defineField({
      name: "footerSocialsLabel",
      title: "Footer socials label",
      type: "string",
      group: "footer",
      description: "Label for the Socials column in the footer (e.g. 'Socials')."
    }),
    defineField({
      name: "footerCopyrightTagline",
      title: "Footer copyright tagline",
      type: "string",
      group: "footer",
      description: "Short tagline shown next to the copyright (e.g. 'Curated fine jewelry')."
    }),
    defineField({
      name: "socialLinks",
      title: "Social links",
      type: "array",
      description: "Add the social profiles you want to show in the footer.",
      of: [
        defineField({
          name: "socialLink",
          title: "Social link",
          type: "object",
          fields: [
            defineField({
              name: "label",
              title: "Label",
              type: "string",
              validation: (Rule) => Rule.required().error("Add the social platform name.")
            }),
            defineField({
              name: "url",
              title: "URL",
              type: "url",
              description: "Leave blank if you do not want to link yet.",
              validation: (Rule) =>
                Rule.uri({
                  allowRelative: false,
                  scheme: ["http", "https"]
                })
            })
          ],
          preview: {
            select: {
              title: "label",
              subtitle: "url"
            }
          }
        })
      ],
      group: "footer"
    }),
    // -- Newsletter (lives in the footer) --
    defineField({
      name: "newsletterHeading",
      title: "Newsletter heading",
      type: "string",
      description: "Eyebrow heading above the newsletter signup (e.g. 'Stay in the Know').",
      group: "newsletter"
    }),
    defineField({
      name: "newsletterBody",
      title: "Newsletter body",
      type: "text",
      rows: 2,
      description: "Short description shown next to the newsletter form.",
      group: "newsletter"
    }),
    defineField({
      name: "newsletterEmailPlaceholder",
      title: "Email placeholder",
      type: "string",
      description: "Placeholder text inside the email input (e.g. 'your@email.com').",
      group: "newsletter"
    }),
    defineField({
      name: "newsletterButtonText",
      title: "Subscribe button text",
      type: "string",
      group: "newsletter"
    }),
    defineField({
      name: "newsletterSuccessMessage",
      title: "Success message",
      type: "string",
      description: "Shown after a visitor successfully subscribes.",
      group: "newsletter"
    }),
    defineField({
      name: "newsletterSubscribedTemplate",
      title: "Already-subscribed template",
      type: "string",
      description: "Shown when the visitor is already known. Use {{email}} as a placeholder for their address.",
      group: "newsletter"
    }),
    // -- Add-to-bag buttons (used on piece detail pages, swipe deck, etc.) --
    defineField({
      name: "addToBagText",
      title: "Add to bag button text",
      type: "string",
      group: "itemActions"
    }),
    defineField({
      name: "soldOutText",
      title: "Sold-out button text",
      type: "string",
      description: "Shown on the bag button when an item is marked sold out.",
      group: "itemActions"
    }),
    defineField({
      name: "addedToBagMessage",
      title: "Added-to-bag confirmation",
      type: "string",
      description: "Shown after a piece is successfully added.",
      group: "itemActions"
    }),
    defineField({
      name: "alreadyInBagMessage",
      title: "Already-in-bag message",
      type: "string",
      description: "Shown when the visitor tries to add an item that's already in their bag.",
      group: "itemActions"
    }),
    defineField({
      name: "cannotAddSoldOutMessage",
      title: "Cannot-add sold-out message",
      type: "string",
      group: "itemActions"
    }),
    defineField({
      name: "addToBagErrorMessage",
      title: "Add-to-bag error message",
      type: "string",
      description: "Shown when storage is unavailable or the action otherwise fails.",
      group: "itemActions"
    }),
    // -- Swipe Deck Popup --
    // Site-wide popup promoting the swipe deck (auto-opens once per visitor).
    defineField({
      name: "swipePopupEyebrow",
      title: "Eyebrow",
      type: "string",
      description: "Small text above the title (e.g. \"✨ New Feature ✨\").",
      initialValue: "✨ New Feature ✨",
      group: "swipeDeckPopup"
    }),
    defineField({
      name: "swipePopupTitle",
      title: "Title",
      type: "string",
      initialValue: "Swipe through the Vault 88 collection",
      group: "swipeDeckPopup"
    }),
    defineField({
      name: "swipePopupSubtitle",
      title: "Subtitle",
      type: "string",
      initialValue: "(in a very familiar way)",
      group: "swipeDeckPopup"
    }),
    defineField({
      name: "swipePopupBody",
      title: "Body",
      type: "text",
      rows: 3,
      initialValue:
        "We’ll look at your likes, dislikes and recommend pieces to suit your individual taste and style.",
      group: "swipeDeckPopup"
    }),
    defineField({
      name: "swipePopupCtaText",
      title: "Start-swiping button text",
      type: "string",
      initialValue: "START SWIPING!",
      group: "swipeDeckPopup"
    }),
    defineField({
      name: "swipePopupDismissText",
      title: "Dismiss button text",
      type: "string",
      initialValue: "back to classic browsing",
      group: "swipeDeckPopup"
    }),
    // -- Onboarding Modal --
    defineField({
      name: "onboardingEyebrow",
      title: "Step 1 eyebrow",
      type: "string",
      description: "Small text above the title on the email step (e.g. \"Stay connected\").",
      group: "onboarding"
    }),
    defineField({
      name: "onboardingTitle",
      title: "Step 1 title",
      type: "string",
      description: "Main heading on the email step (e.g. \"Save your preferences\").",
      group: "onboarding"
    }),
    defineField({
      name: "onboardingSubtitle",
      title: "Step 1 description",
      type: "text",
      rows: 3,
      description: "Body text below the title on the email step.",
      group: "onboarding"
    }),
    defineField({
      name: "onboardingEmailLabel",
      title: "Email field label",
      type: "string",
      description: "Label above the email input (e.g. \"Email address\").",
      group: "onboarding"
    }),
    defineField({
      name: "onboardingEmailPlaceholder",
      title: "Email placeholder",
      type: "string",
      description: "Placeholder text inside the email input (e.g. \"you@example.com\").",
      group: "onboarding"
    }),
    defineField({
      name: "onboardingButtonText",
      title: "Step 1 button text",
      type: "string",
      description: "Text on the Continue button (e.g. \"Continue\").",
      group: "onboarding"
    }),
    defineField({
      name: "onboardingNameEyebrow",
      title: "Step 2 eyebrow",
      type: "string",
      description: "Small text above the title on the name step (e.g. \"One last step\").",
      group: "onboarding"
    }),
    defineField({
      name: "onboardingNameTitle",
      title: "Step 2 title",
      type: "string",
      description: "Main heading on the name step (e.g. \"Tell us about you\").",
      group: "onboarding"
    }),
    defineField({
      name: "onboardingNameSubtitle",
      title: "Step 2 description",
      type: "text",
      rows: 3,
      description: "Body text below the title on the name step.",
      group: "onboarding"
    }),
    defineField({
      name: "onboardingFirstNameLabel",
      title: "First name label",
      type: "string",
      description: "Label for the first name input (e.g. \"First name\").",
      group: "onboarding"
    }),
    defineField({
      name: "onboardingFirstNamePlaceholder",
      title: "First name placeholder",
      type: "string",
      description: "Placeholder text for the first name input (e.g. \"Alex\").",
      group: "onboarding"
    }),
    defineField({
      name: "onboardingLastNameLabel",
      title: "Last name label",
      type: "string",
      description: "Label for the last name input (e.g. \"Last name\").",
      group: "onboarding"
    }),
    defineField({
      name: "onboardingLastNamePlaceholder",
      title: "Last name placeholder",
      type: "string",
      description: "Placeholder text for the last name input (e.g. \"Morgan\").",
      group: "onboarding"
    }),
    defineField({
      name: "onboardingNameButtonText",
      title: "Step 2 submit button text",
      type: "string",
      description: "Text on the Create / submit button (e.g. \"Create\").",
      group: "onboarding"
    }),
    defineField({
      name: "onboardingSkipText",
      title: "Skip button text",
      type: "string",
      description: "Text on the Skip button (e.g. \"Skip\").",
      group: "onboarding"
    }),
    defineField({
      name: "onboardingWelcomeText",
      title: "Welcome message (new user)",
      type: "string",
      description: "Welcome message for new users. Use {{name}} for the user\u2019s name (e.g. \"Welcome, {{name}}\"). Shown without the name placeholder if no name is available.",
      group: "onboarding"
    }),
    defineField({
      name: "onboardingWelcomeBackText",
      title: "Welcome back message (returning user)",
      type: "string",
      description: "Welcome message for returning users. Use {{name}} for the user\u2019s name (e.g. \"Welcome back, {{name}}\").",
      group: "onboarding"
    }),
    defineField({
      name: "onboardingWelcomeFallback",
      title: "Welcome fallback (no name)",
      type: "string",
      description: "Shown when the user\u2019s name is not available (e.g. \"Welcome to Vault 88\").",
      group: "onboarding"
    }),
    defineField({
      name: "onboardingWelcomeBackFallback",
      title: "Welcome back fallback (no name)",
      type: "string",
      description: "Shown for returning users when the name is not available (e.g. \"Welcome back to Vault 88\").",
      group: "onboarding"
    }),
    defineField({
      name: "necklaceSizeChartImage",
      title: "Necklace size chart image",
      type: "image",
      description: "Size chart image shown on necklace product pages.",
      options: { hotspot: true },
      fields: [
        defineField({
          name: "alt",
          title: "Alt text",
          type: "string",
          description: "Describe the image for screen readers."
        })
      ],
      group: "branding"
    }),
    // -- Invoices (config + brand text for the invoice PDF / Studio form) --
    defineField({
      name: "defaultTaxRate",
      title: "Default sales tax rate (%)",
      type: "number",
      description: "Prefills the tax rate on new retail invoices (e.g. 8.25).",
      validation: (Rule) => Rule.min(0).max(100).precision(2),
      group: "invoices"
    }),
    defineField({
      name: "stateTaxRates",
      title: "Sales tax rate overrides by state (%)",
      type: "array",
      description:
        "OVERRIDES ONLY — you do not need to fill this in. All 50 states and DC already have a built-in rate, so selecting a state on an order or invoice prefills it automatically. Add a row here only to correct one: a rate that has changed, or a state where you want to include the county/city add-on (the built-in rates are state-level only, so they under-charge in places like Louisiana and Alabama). A row you add always wins over the built-in rate.",
      of: [
        defineArrayMember({
          type: "object",
          name: "stateTaxRate",
          fields: [
            defineField({
              name: "state",
              title: "State",
              type: "string",
              options: {
                list: US_STATES.map((s) => ({ title: s.name, value: s.code })),
              },
              validation: (Rule) => Rule.required()
            }),
            defineField({
              name: "rate",
              title: "Rate (%)",
              type: "number",
              validation: (Rule) => Rule.required().min(0).max(100).precision(3)
            })
          ],
          preview: {
            select: { state: "state", rate: "rate" },
            prepare: ({ state, rate }: { state?: string; rate?: number }) => ({
              title: state ?? "(no state)",
              subtitle: rate != null ? `${rate}%` : "(no rate)"
            })
          }
        })
      ],
      group: "invoices"
    }),
    defineField({
      name: "founderName",
      title: "Founder name",
      type: "string",
      description: "Shown in the invoice signature block (e.g. Dana Michelle).",
      group: "invoices"
    }),
    defineField({
      name: "founderTitle",
      title: "Founder title",
      type: "string",
      description: "Shown under the founder name (e.g. Private Jeweler and Designer).",
      initialValue: "Private Jeweler and Designer",
      group: "invoices"
    }),
    defineField({
      name: "contactEmail",
      title: "Invoice contact email",
      type: "string",
      description: "Contact email shown on invoices.",
      group: "invoices"
    }),
    defineField({
      name: "contactPhone",
      title: "Invoice contact phone",
      type: "string",
      description: "Contact phone shown on invoices.",
      group: "invoices"
    }),
    defineField({
      name: "careReturnsLine",
      title: "Care & returns line",
      type: "string",
      description: "Footer line on retail invoices (e.g. care & returns info link).",
      group: "invoices"
    }),
    defineField({
      name: "updatedAt",
      title: "Last updated",
      type: "datetime",
      description: "Read-only timestamp for internal tracking.",
      readOnly: true
    })
  ],
  initialValue: () => ({
    title: "Site Settings",
    brandName: "Vault 88",
    primaryColor: "#c4a668",
    secondaryColor: "#1e1b16",
    headerFontFamily: "inter",
    bodyFontFamily: "inter",
    baseFontScale: 1,
    collectionsMenuTitle: "Collections",
    collectionsMenuItems: [],
    footerTagline: "NATURAL STONES.\nSOLID GOLD.",
    privateShowingsHeading: "IN PERSON",
    privateShowingsBody: "AVAILABLE BY APPOINTMENT OR PRIVATE EVENT",
    privateShowingsCtaText: "BOOK A SHOWING",
    privateShowingsCtaLink: "/contact",
    footerExploreLabel: "Explore",
    footerSocialsLabel: "Socials",
    footerCopyrightTagline: "Curated fine jewelry",
    updatedAt: new Date().toISOString()
  }),
  preview: {
    prepare: () => ({
      title: "Site Settings"
    })
  }
});
